import '../styles/globals.css';

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Helmet from 'react-helmet';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { QueryClient, QueryClientProvider } from '@zg-rentals/react-query5';

// @zillow / @zg-rentals
import { Box } from '@zillow/constellation-10';
import { BlockedUserModal } from '@zg-rentals/blocked-user-modal';
import { RentalsB2BLayout } from '@zg-rentals/b2b-layout';
import { useSiftScience } from '@zg-rentals/sift-science';

// Actions
import AppActions from '../flux/actions/AppActions';

// Misc / Utils
import { getDevice } from '../utils/browserUtils';
import getRoutes from '../utils/routes';

// Types
import type { State } from '../types/State';
import { trackComscoreEvent, trackUserEvent } from 'app/client/analytics';
import { useZrmPageViewEvents } from '@zg-rentals/clickstream';
import { useComscorePageTracking } from '@zg-rentals/comscore';

const metaDescriptionContent =
  'Use Zillow Rental Manager to manage applications, screen tenants, collect rent and list ' +
  'your rental property on Zillow, Trulia and HotPads.';
const METAS = [{ name: 'description', content: metaDescriptionContent }];
const TITLE = 'Price your rental - Start here – Rent comp estimator | Zillow Rental Manager';

const App = () => {
  // Redux
  const dispatch = useDispatch();
  const location = useLocation();

  const { user, asPath, pathname, fromApp } = useSelector(
    (state: State) => ({
      user: state.user,
      asPath: state.router.location.path,
      pathname: state.router.location.pathname,
      fromApp: state.app.fromApp,
    }),
    shallowEqual,
  );
  useSiftScience({ email: user?.email, isImpersonator: Boolean(user?.impersonatorUserToken) });
  useZrmPageViewEvents(trackUserEvent, location.pathname, user);
  useComscorePageTracking(() => trackComscoreEvent({}), location.pathname);

  const setDeviceAndWidth = debounce(() => {
    const device = getDevice();
    dispatch(AppActions.updateApp({ device }));
  }, 100);

  useEffect(() => {
    setDeviceAndWidth();
    window.addEventListener('resize', setDeviceAndWidth);
  }, []);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 5 * (60 * 1000), // 5 mins
        throwOnError: true,
        retry: 0,
      },
    },
  });

  const pathNameWithoutTrailingSlash = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  return (
    <QueryClientProvider client={queryClient}>
      <Box css={{ height: '100%', minHeight: '100vh', width: '100%' }}>
        <Helmet meta={METAS} title={TITLE} />
        <RentalsB2BLayout header={{ fromApp }} router={{ asPath, pathname: pathNameWithoutTrailingSlash }}>
          {getRoutes()}
        </RentalsB2BLayout>
        {user?.restrictions?.isAllowed === false && user != null && <BlockedUserModal user={user} />}
      </Box>
    </QueryClientProvider>
  );
};

export default App;
